import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import {
    AccountData,
    AATransaction
} from '../../shared/models/account-aggregator.interface';
import { Transaction } from '../../shared/models/data.interface';

@Injectable({
    providedIn: 'root'
})
export class AccountAggregatorService {

    private apiUrl = environment.accountAggregator.baseUrl;
    // private endpoints = environment.accountAggregator.endpoints;
    private apiHeaders = environment.accountAggregator.headers;

    constructor(private http: HttpClient) { }

    /**
     * Download statement PDF
     * @param consentID The consent ID
     * @param linkRefNumber Array of link reference numbers
     * @returns Observable of Blob (PDF file)
     */
    downloadStatement(consentID: string, linkRefNumber: string[]): Observable<Blob> {
        // Use the new moneyone proxy url
        // const url = `${this.apiUrl}${this.endpoints.getStatement}`;
        const url = `${this.apiUrl}/statement/download`;

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'client_id': this.apiHeaders.clientId,
            'client_secret': this.apiHeaders.clientSecret,
            'organisationId': this.apiHeaders.organisationId,
            'appIdentifier': this.apiHeaders.appIdentifier
        });

        const body = {
            consentID: consentID,
            linkRefNumber: linkRefNumber
        };

        return this.http.post(url, body, {
            headers: headers,
            responseType: 'blob'
        });
    }

    /**
     * Parse API response and extract all accounts
     * Handles different API response formats safely
     */
    parseAccountAggregatorResponse(response: any): AccountData[] {
        if (!response || response.status !== 'success' || !response.data) {
            console.error('Invalid API response:', response);
            return [];
        }
        return response.data;
    }

    /**
     * Get all transactions from all accounts
     */
    getAllTransactions(accounts: any[]): any[] {
        const allTransactions: any[] = [];

        accounts.forEach(account => {
            if (account.Transactions && account.Transactions.Transaction) {
                allTransactions.push(...account.Transactions.Transaction);
            }
        });

        return allTransactions;
    }

    /**
     * Transform Account Aggregator transaction to application Transaction interface
     */
    transformAATransactionToTransaction(
        aaTransaction: AATransaction,
        accountData?: AccountData,
        index?: number
    ): Transaction {
        // Safety checks
        if (!aaTransaction) {
            throw new Error('Transaction data is null or undefined');
        }

        // Parse date safely
        const rawDate = aaTransaction.transactionTimestamp || (aaTransaction as any).valueDate || '';
        let timestamp = new Date(rawDate);

        if (isNaN(timestamp.getTime()) && typeof rawDate === 'string' && rawDate) {
            // Attempt DD/MM/YYYY or DD-MM-YYYY parsing
            const datePart = rawDate.split('T')[0].split(' ')[0];
            const separator = datePart.includes('/') ? '/' : datePart.includes('-') ? '-' : null;
            if (separator) {
                const parts = datePart.split(separator);
                if (parts.length === 3) {
                    const firstPart = parseInt(parts[0], 10);
                    if (firstPart <= 31) {
                        const d = firstPart;
                        const m = parseInt(parts[1], 10) - 1;
                        let y = parseInt(parts[2], 10);
                        if (y < 100) y += 2000;
                        timestamp = new Date(y, m, d);
                    }
                }
            }
        }

        // Format date like "14 JUN 5:35AM" or fallback
        const isValidDate = !isNaN(timestamp.getTime());
        const formattedDate = isValidDate ? this.formatTransactionDate(timestamp) : (rawDate || 'N/A');

        // Determine transaction type
        const type = aaTransaction.type === 'CREDIT' ? 'deposit' : 'withdrawal';

        // Format amount with symbol
        // const amountPrefix = aaTransaction.type === 'CREDIT' ? '+' : '-';
        // const formattedAmount = `${amountPrefix}₹${this.formatAmount(aaTransaction.amount || '0')}`;
        const rawAmount = parseFloat(aaTransaction.amount || '0');
        const signedAmount = aaTransaction.type === 'CREDIT' ? rawAmount : -rawAmount;

        // Extract category from narration
        const narration = aaTransaction.narration || 'Unknown Transaction';
        const category = this.extractCategoryFromNarration(narration, aaTransaction.mode, aaTransaction.type);

        // Determine status (simplified - you may need more sophisticated logic)
        const status = this.determineStatus(aaTransaction);

        // Format balance
        const formattedBalance = `₹${this.formatAmount(aaTransaction.currentBalance || '0')}`;

        // Extract card number from account
        const cardNumber = accountData?.maskedAccountNumber?.slice(-4) || '';

        return {
            id: index !== undefined ? index + 1 : 0,
            name: this.extractNameFromNarration(narration, aaTransaction.mode, aaTransaction.type),
            type: type,
            date: formattedDate,
            status: status,
            category: category,
            amount: signedAmount,
            balance: formattedBalance,
            cardNumber: cardNumber,
            txnId: aaTransaction.txnId,
            reference: aaTransaction.reference,
            currency: accountData?.Summary?.currency || 'INR',
            timestamp: timestamp
        };
    }

    /**
     * Transform array of AA transactions to Transaction array
     */
    transformAATransactionsToTransactions(
        aaTransactions: AATransaction[],
        accountData?: AccountData
    ): Transaction[] {
        return aaTransactions.map((aaTransaction, index) => {
            try {
                return this.transformAATransactionToTransaction(aaTransaction, accountData, index);
            } catch (error) {
                console.error('Error transforming transaction:', aaTransaction, error);
                return null;
            }
        }).filter(t => t !== null) as Transaction[];
    }

    /**
     * Transform all transactions from all accounts
     * This preserves the account association for each transaction
     */
    transformAllAccountsTransactions(accounts: AccountData[]): Transaction[] {
        const allTransactions: Transaction[] = [];
        let globalIndex = 0;

        accounts.forEach(account => {
            if (account.Transactions && account.Transactions.Transaction) {
                const accountTransactions = account.Transactions.Transaction.map((aaTransaction) => {
                    try {
                        return this.transformAATransactionToTransaction(aaTransaction, account, globalIndex++);
                    } catch (error) {
                        console.error('Error transforming transaction:', aaTransaction, error);
                        return null;
                    }
                }).filter(t => t !== null) as Transaction[];

                allTransactions.push(...accountTransactions);
            }
        });

        return allTransactions;
    }


    /**
     * Format date in readable format
     */
    private formatTransactionDate(date: Date): string {
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

        const day = date.getDate();
        const month = months[date.getMonth()];

        // Hide time if exactly midnight (indicates a date-only transaction)
        if (date.getHours() === 0 && date.getMinutes() === 0) {
            //return `${day} ${month}`;
        }

        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'

        const minutesStr = minutes < 10 ? '0' + minutes : minutes;

        return `${day} ${month} ${hours}:${minutesStr}${ampm}`;
    }

    /**
     * Format amount with commas
     */
    private formatAmount(amount: any): string {
        if (amount === undefined || amount === null) return '0.00';

        const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

        if (isNaN(numAmount) || typeof numAmount !== 'number') return '0.00';

        return numAmount.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    /**
     * Extract transaction name from narration
     */
    /**
     * Extract transaction name from narration
     */
    private extractNameFromNarration(narration: string, mode?: string, type?: string): string {
        // Try to extract meaningful name from narration

        // Prioritize Mode if available for specific types
        if (mode === 'ATM') {
            if (type === 'CREDIT') {
                return 'ATM Transaction';
            } else if (type === 'DEBIT') {
                return 'ATM Withdrawal';
            }
            return 'ATM Transaction'; // Default fallback
        }

        // UPI Debit (when mode is OTHERS)
        if (mode === 'OTHERS' && type === 'DEBIT' && narration.includes('UPI')) {
            return 'UPI Payment';
        }

        if (mode === 'OTHERS' && type === 'DEBIT') {
            return 'Online Transfer';
        }

        // IMPS Credit
        if (mode === 'OTHERS' && type === 'CREDIT' && narration.includes('IMPSAB/')) {
            return 'Online Transfer';
        }

        // Cash Deposit
        if (mode === 'CASH' && type === 'CREDIT') {
            return 'Cash Deposit';
        }

        // UPI Credit (when mode is OTHERS)
        if (mode === 'OTHERS' && type === 'CREDIT' && narration.includes('UPI')) {
            return 'UPI Transfer';
        }

        // UPI Mode
        if (mode === 'UPI') {
            if (type === 'CREDIT') return 'UPI Transfer';
            if (type === 'DEBIT') return 'UPI Payment';
        }

        // Card Debit with UPI in narration
        if (mode === 'CARD' && type === 'DEBIT' && narration.includes('UPI')) {
            return 'UPI Payment';
        }

        // Card Credit with UPI in narration
        if (mode === 'CARD' && type === 'CREDIT' && narration.includes('UPI')) {
            return 'UPI Transfer';
        }

        // Card mode with IMPS in narration
        if (mode === 'CARD' && type === 'CREDIT' && narration.includes('IMPSAB')) {
            return 'Online Transfer';
        }

        // Cash Debit with UPI in narration
        if (mode === 'CASH' && type === 'DEBIT' && narration.includes('UPI')) {
            return 'UPI Payment';
        }

        // Interest Payment
        if (narration.includes('Int.Pd')) {
            return 'Interest Credited';
        }

        if (narration.includes('UPI/')) {
            const parts = narration.split('/');
            if (parts.length > 3) {
                return `UPI - ${parts[3]}`;
            }
        }

        if (narration.includes('IMPSAB/')) {
            const match = narration.match(/IMPSAB\/\d+\/(.+?)\d/);
            if (match) {
                return `IMPS - ${match[1].trim()}`;
            }
        }

        if (narration.includes('ATM/') || mode === 'ATM') {
            return 'ATM Withdrawal';
        }

        if (narration.includes('CARD/') || mode === 'CARD') {
            return 'Card Transaction';
        }

        if (narration.includes('FT/')) {
            return 'Fund Transfer';
        }

        if (narration.includes('CASH/') || mode === 'CASH') {
            return 'Cash Transaction';
        }

        // Default to narration if no pattern matches (truncated)
        return narration.length > 30 ? narration.substring(0, 30) + '...' : narration;
    }

    /**
     * Extract category from narration
     * Based on actual API data patterns - most transactions are person-to-person transfers
     * without merchant category information
     */
    private extractCategoryFromNarration(narration: string, mode?: string, type?: string): string {
        const lowerNarration = narration.toLowerCase();

        // INCOME - Interest payments and salary
        if (lowerNarration.includes('int.pd') || lowerNarration.includes('interest')) {
            return 'INCOME';
        }
        if (lowerNarration.includes('salary') || lowerNarration.includes('income')) {
            return 'INCOME';
        }

        // UTILITIES - Bank charges, SMS charges, fees
        if (lowerNarration.includes('sms charges') ||
            lowerNarration.includes('bank charges') ||
            lowerNarration.includes('service charges') ||
            lowerNarration.includes('annual fee') ||
            lowerNarration.includes('processing fee')) {
            return 'UTILITIES';
        }

        // REFUND - Explicit refunds or reversals
        if (lowerNarration.includes('refund') ||
            lowerNarration.includes('reversal') ||
            lowerNarration.includes('cashback')) {
            return 'REFUND';
        }

        // FOOD - Food delivery and dining (if merchant names are present)
        if (lowerNarration.includes('zomato') ||
            lowerNarration.includes('swiggy') ||
            lowerNarration.includes('restaurant') ||
            lowerNarration.includes('food') ||
            lowerNarration.includes('cafe') ||
            lowerNarration.includes('pizza') ||
            lowerNarration.includes('burger')) {
            return 'FOOD';
        }

        // TRANSPORT - Travel and transportation
        if (lowerNarration.includes('uber') ||
            lowerNarration.includes('ola') ||
            lowerNarration.includes('rapido') ||
            lowerNarration.includes('petrol') ||
            lowerNarration.includes('fuel') ||
            lowerNarration.includes('parking') ||
            lowerNarration.includes('toll')) {
            return 'TRANSPORT';
        }

        // ENTERTAINMENT - Movies, streaming, games
        if (lowerNarration.includes('netflix') ||
            lowerNarration.includes('amazon prime') ||
            lowerNarration.includes('spotify') ||
            lowerNarration.includes('movie') ||
            lowerNarration.includes('pvr') ||
            lowerNarration.includes('inox') ||
            lowerNarration.includes('bookmyshow')) {
            return 'ENTERTAINMENT';
        }

        // HEALTHCARE - Medical and pharmacy
        if (lowerNarration.includes('pharma') ||
            lowerNarration.includes('medical') ||
            lowerNarration.includes('hospital') ||
            lowerNarration.includes('clinic') ||
            lowerNarration.includes('apollo') ||
            lowerNarration.includes('medplus')) {
            return 'HEALTHCARE';
        }

        // AEPS withdrawals (cash withdrawals via merchant)
        if (lowerNarration.includes('aeps-onus-cw') || lowerNarration.includes('aeps')) {
            return 'OVERALL BUDGET';
        }

        // ATM withdrawals
        if (mode === 'ATM' || lowerNarration.includes('atm')) {
            return 'OVERALL BUDGET';
        }

        // Cash transactions
        if (mode === 'CASH' || lowerNarration.includes('cash deposit')) {
            return 'OVERALL BUDGET';
        }

        // Default: Most API transactions are person-to-person transfers without merchant info
        // We categorize these as OVERALL BUDGET since we cannot determine the actual purpose
        return 'OVERALL BUDGET';
    }

    /**
     * Determine transaction status
     */
    private determineStatus(aaTransaction: AATransaction): 'SUCCESS' | 'FAILED' | 'PROCESS' | 'PENDING' {
        // In real scenario, you would have actual status field
        // For now, assuming all completed transactions are SUCCESS
        // You may need to check narration for failures or pending states

        const narration = (aaTransaction.narration || '').toLowerCase();

        if (narration.includes('fail') || narration.includes('reject')) {
            return 'FAILED';
        }

        if (narration.includes('pending') || narration.includes('process')) {
            return 'PROCESS';
        }

        return 'SUCCESS';
    }

    /**
     * Get account summary statistics
     */
    getAccountSummaryStats(accounts: AccountData[]): {
        totalBalance: number;
        totalAccounts: number;
        activeAccounts: number;
        totalTransactions: number;
    } {
        let totalBalance = 0;
        let totalTransactions = 0;
        let activeAccounts = 0;

        accounts.forEach(account => {
            // Handle optional Summary field
            if (account.Summary) {
                totalBalance += parseFloat(account.Summary.currentBalance || '0');

                if (account.Summary.status === 'ACTIVE') {
                    activeAccounts++;
                }
            }

            if (account.Transactions && account.Transactions.Transaction) {
                totalTransactions += account.Transactions.Transaction.length;
            }
        });

        return {
            totalBalance,
            totalAccounts: accounts.length,
            activeAccounts,
            totalTransactions
        };
    }

    /**
     * Filter transactions by date range
     */
    filterTransactionsByDateRange(
        transactions: AATransaction[],
        startDate: Date,
        endDate: Date
    ): AATransaction[] {
        return transactions.filter(transaction => {
            const transactionDate = new Date(transaction.transactionTimestamp);
            return transactionDate >= startDate && transactionDate <= endDate;
        });
    }

    /**
     * Filter transactions by type (CREDIT/DEBIT)
     */
    filterTransactionsByType(
        transactions: AATransaction[],
        type: 'CREDIT' | 'DEBIT'
    ): AATransaction[] {
        return transactions.filter(transaction => transaction.type === type);
    }

    /**
     * Filter transactions by mode
     */
    filterTransactionsByMode(
        transactions: AATransaction[],
        mode: string
    ): AATransaction[] {
        return transactions.filter(transaction => transaction.mode === mode);
    }

    /**
     * Get transactions for specific account
     */
    getAccountTransactions(accountData: AccountData): AATransaction[] {
        if (!accountData.Transactions || !accountData.Transactions.Transaction) {
            return [];
        }
        return accountData.Transactions.Transaction;
    }

    /**
     * Calculate total credits and debits
     */
    calculateTotals(transactions: AATransaction[]): { totalCredit: number; totalDebit: number; net: number } {
        let totalCredit = 0;
        let totalDebit = 0;

        transactions.forEach(transaction => {
            const amount = parseFloat(transaction.amount);

            if (transaction.type === 'CREDIT') {
                totalCredit += amount;
            } else {
                totalDebit += amount;
            }
        });

        return {
            totalCredit,
            totalDebit,
            net: totalCredit - totalDebit
        };
    }

    /**
     * Get unique account numbers for card dropdown
     */
    getUniqueAccountNumbers(accounts: AccountData[]): { value: string; label: string }[] {
        const uniqueAccounts = new Map<string, string>();

        accounts.forEach(account => {
            if (account.maskedAccountNumber) {
                const last4Digits = account.maskedAccountNumber.slice(-4);
                const formattedLabel = `**** **** **** ${last4Digits}`;
                uniqueAccounts.set(last4Digits, formattedLabel);
            }
        });

        return Array.from(uniqueAccounts.entries()).map(([value, label]) => ({
            value,
            label
        }));
    }

    /**
     * Transform accounts data for Account Summary Widget
     * Formats the API response into the structure required by the AccountSummary component
     */
    transformAccountsForSummaryWidget(accounts: AccountData[]): any[] {
        return accounts.map(account => {
            // Get masked account number (last 4 digits)
            const last4Digits = account.maskedAccountNumber?.slice(-4) || '0000';
            const accountNumber = `**** ${last4Digits}`;

            // Get current balance from Summary
            const balance = account.Summary?.currentBalance || '0';
            const formattedBalance = this.formatAmount(balance);

            // Get currency
            const currency = account.Summary?.currency || 'INR';

            // Get transactions (limit to recent 4 for the widget)
            const transactions = this.getAccountTransactions(account)
                .slice(0, 4) // Get only the 4 most recent transactions
                .map(aaTransaction => {
                    // Handle potential invalid date
                    const timestamp = new Date(aaTransaction.transactionTimestamp);
                    const isValidDate = !isNaN(timestamp.getTime());

                    const type = aaTransaction.type === 'CREDIT' ? 'deposit' : 'withdraw';

                    // Format date like "14 JUN" using UTC
                    let date = 'N/A';
                    let time = 'N/A';

                    if (isValidDate) {
                        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                        const day = timestamp.getUTCDate();
                        const month = months[timestamp.getUTCMonth()];
                        date = `${day} ${month}`;

                        // Only show time if it's not exactly midnight (common in date-only strings)
                        if (timestamp.getUTCHours() !== 0 || timestamp.getUTCMinutes() !== 0) {
                            let hours = timestamp.getUTCHours();
                            const minutes = timestamp.getUTCMinutes();
                            const ampm = hours >= 12 ? 'PM' : 'AM';
                            hours = hours % 12;
                            hours = hours ? hours : 12;
                            const minutesStr = minutes < 10 ? '0' + minutes : minutes;
                            time = `${hours}:${minutesStr}${ampm}`;
                        } else {
                            time = ''; // Hide time for date-only transactions
                        }
                    } else if (aaTransaction.valueDate || aaTransaction.transactionTimestamp) {
                        // Try fallback parsing for raw strings
                        const rawDateStr = aaTransaction.valueDate || aaTransaction.transactionTimestamp;
                        const fallbackDate = new Date(rawDateStr);
                        if (!isNaN(fallbackDate.getTime())) {
                            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                            date = `${fallbackDate.getUTCDate()} ${months[fallbackDate.getUTCMonth()]}`;
                            time = '';
                        }
                    }

                    if (date === 'N/A') time = ''; // Don't show "N/A N/A"

                    // Format amount with fallback for different FI types
                    const txAny = aaTransaction as any;
                    const rawAmount = txAny.amount || txAny.balance || txAny.currentValue || 0;
                    const amount = `₹${this.formatAmount(rawAmount)}`;

                    // Get transaction label/name
                    const label = this.extractNameFromNarration(
                        aaTransaction.narration,
                        aaTransaction.mode,
                        aaTransaction.type
                    );

                    return {
                        icon: 'assets/icons/arrow-down.svg',
                        label,
                        date,
                        time,
                        amount,
                        type
                    };
                });

            return {
                number: accountNumber,
                balance: formattedBalance,
                currency,
                transactions
            };
        });
    }

    /**
     * Transform accounts data for Account Summary Modal (AccountItem format)
     * Separates accounts and credit cards based on account type
     */
    transformAccountsForModal(accounts: AccountData[]): {
        accounts: any[],
        creditCards: any[]
    } {
        const accountItems: any[] = [];
        const creditCardItems: any[] = [];

        accounts.forEach((account, index) => {
            const last4Digits = account.maskedAccountNumber?.slice(-4) || '0000';
            const cardNumber = last4Digits;
            const balance = account.Summary?.currentBalance || '0';
            const formattedBalance = `₹${this.formatAmount(balance)}`;

            // Determine account type/name with bank name
            const accountType = account.Summary?.type || account.fiType || 'Savings';
            const bankName = account.bank || 'Unknown Bank';
            const displayName = `${accountType}`; // Show bank name + account type
            const isCredit = accountType?.toLowerCase().includes('credit') ||
                accountType?.toLowerCase().includes('card');

            const item = {
                id: account.linkReferenceNumber || `acc_${index}`,
                name: displayName,
                cardNumber: cardNumber,
                amount: formattedBalance,
                selected: true // Default: select all accounts
            };

            if (isCredit) {
                // For credit cards, you might want to add usedAmount and amountColor
                creditCardItems.push(item);
            } else {
                if (account.fiType === 'DEPOSIT') {
                    accountItems.push(item);
                }
            }
        });

        return {
            accounts: accountItems,
            creditCards: creditCardItems
        };
    }
}
